import { create } from 'zustand';
import useLoadingStore from './use-loading';
import { makeRequest } from 'api/api-client';
import { HTTP } from 'constants/http-method';
import { API_ENDPOINTS } from 'api/endpoints';

const useStaffMemberComplianceStore = create((set) => ({
  documents: [],
  compliance: [],
  loadingCompliance: false,
  pagination: {
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  },

  // Function to get compliance categories
  getComplianceRequirementCategories: async ({ id, successCallback }) => {
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_COMPLIANCE_REQUIREMENT_CATEGORIES,
        null,
        null,
        id
      );

      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to get compliance requirements by category
  getComplianceRequirementByCategory: async ({
    id,
    query = null,
    successCallback,
  }) => {
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_ALL_COMPLIANCE_REQUIREMENT,
        null,
        query,
        id
      );

      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to get compliance
  getCompliance: async ({ id, query = null, successCallback }) => {
    set({ loadingCompliance: true });
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_COMPLIANCE,
        null,
        query,
        id
      );

      if (response.success) {
        set({
          compliance: response.data?.data || [],
          pagination: {
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalPages,
          },
        });
        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      set({ loadingCompliance: false });
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to get documents by compliance
  getDocumentsByComplianceId: async ({ id, query = null, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_DOCUMENTS_BY_COMPLIANCE_ID,
        null,
        query,
        id
      );

      if (response.success) {
        set({
          documents: response.data?.data || [],
        });
        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to create the compliance
  createCompliance: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.CREATE_COMPLIANCE,
        data
      );

      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete the compliance
  deleteCompliance: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.DELETE_COMPLIANCE,
        null,
        null,
        id
      );

      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete the compliance
  deleteComplianceDocument: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.DELETE_COMPLIANCE_DOCUMENT,
        null,
        null,
        id
      );

      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to update the compliance
  updateCompliance: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.UPDATE_COMPLIANCE,
        data,
        null,
        id
      );

      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
      // Handle error
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to upload the new compliance
  uploadNewCompliance: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.UPLOAD_NEW_COMPLIANCE,
        data
      );

      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
      // Handle error
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to toggle the status of compliance
  toggleStatusOfCompliance: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.UPDATE_COMPLIANCE_APPROVAL,
        data,
        null,
        id
      );
      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to send reminder
  complianceSendReminder: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.COMPLIANCE_SEND_REMINDER,
        null,
        null,
        id
      );

      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },
}));

export default useStaffMemberComplianceStore;
